<template>
  <FadeTransition>
    <MFALogin
      v-if="mfaInfo"
      :on-mfa-login-success="handleOnMFASuccess"
      :info-mfa="mfaInfo"
      :login-data="loginData"
    />
    <div v-else>
      <SigninTemplate>
        <template #header>
          <BrandImage
            class="login__logo"
            :image="EBrandImageLogos.WARREN_PRIMARY"
          />
        </template>
        <template #default>
          <main class="login__main">
            <Heading :level="3" as="h1" class="login__heading">
              Acessar minha conta
            </Heading>
            <form class="login__form" @submit.prevent="handleSubmit">
              <InputText
                id="email"
                v-model="formData.email.value"
                class="login__input"
                label="E-mail"
                leading-icon="ic_mail"
                type="email"
                :status="formData.email.status"
                :helper-text="formData.email.helperText"
                @input="handleInputChange"
                @blur="handleOnBlur"
              />
              <InputPassword
                id="password"
                v-model="formData.password.value"
                class="login__input"
                label="Senha"
                :status="formData.password.status"
                :helper-text="formData.password.helperText"
                @input="handleInputChange"
                @blur="handleOnBlur"
              />
              <ButtonPrimary
                size="large"
                label="Entrar"
                type="submit"
                :loading="isLoading"
              />
            </form>
            <div class="login__divider" />
            <!-- <div class="login__links">
              <a class="login__link" :href="resetPasswordHref">
                Esqueci minha senha
              </a>
              <a class="login__link" :href="signUpHref">
                Ainda não sou cliente
              </a>
            </div> -->
          </main>
        </template>
      </SigninTemplate>
      <Recaptcha ref="recaptcha" @token="onRecaptchaToken" />
    </div>
  </FadeTransition>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router/composables';

import {
  BrandImage,
  EBrandImageLogos,
  SigninTemplate,
  Heading,
  InputText,
  InputPassword,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { MFALogin } from '@/modules/authentication/views/SecurityView/components/MFALogin';
import { FadeTransition } from '@/modules/common/components/FadeTransition';
import {
  Recaptcha,
  RecaptchaActions
} from '@/modules/authentication/components/Recaptcha';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import { login } from '@/modules/common/services/http/account';
import { InputStatusType, FormField } from './types';
import { emailSchema, passwordSchema } from './schemas';
import {
  IMFAInfo,
  IMFALoginResponse
} from '@/modules/authentication/services/mfa';
import { logError } from '@/modules/trade/helpers/logs';

const formData = ref<Record<string, FormField>>({
  email: {
    value: '',
    status: InputStatusType.Default,
    helperText: ''
  },
  password: {
    value: '',
    status: InputStatusType.Default,
    helperText: ''
  }
});
const mfaInfo = ref<IMFAInfo | null>(null);
const isLoading = ref(false);
const recaptcha = ref<InstanceType<typeof Recaptcha> | null>(null);
const recaptchaToken = ref('');

// const router = useRouter();
const route = useRoute();

// const resetPasswordHref = computed(() => {
//   return router.resolve({ name: 'reset' }).href;
// });

// const signUpHref = computed(() => {
//   return router.resolve({ name: 'fastRegister' }).href;
// });

const hasFieldError = computed(() => {
  return Object.values(formData.value).some(
    field => field.status === InputStatusType.Error
  );
});

const loginData = computed(() => {
  return {
    email: formData.value.email.value,
    password: formData.value.password.value
  };
});

const handleInputChange = () => {
  if (hasFieldError.value) validateFields();
};

const handleOnBlur = () => {
  validateFields();
};

const validateFields = () => {
  try {
    emailSchema.validateSync(formData.value.email.value);

    formData.value.email.status = InputStatusType.Default;
    formData.value.email.helperText = '';
  } catch {
    formData.value.email.status = InputStatusType.Error;
    formData.value.email.helperText = 'E-mail inválido';
  }

  try {
    passwordSchema.validateSync(formData.value.password.value);

    formData.value.password.status = InputStatusType.Default;
    formData.value.password.helperText = '';
  } catch {
    formData.value.password.status = InputStatusType.Error;
    formData.value.password.helperText = 'Senha inválida';
  }
};

const onRecaptchaToken = (token: string) => {
  recaptchaToken.value = token;
  handleLogin();
};

const handleOnMFASuccess = (data: IMFALoginResponse) => {
  generateHBToken(data.accessToken);
};

const generateHBToken = async (accessToken: string) => {
  try {
    const redirectUrl = route.query.url;

    const data = await fetch(
      `${process.env.VUE_APP_BFF_URL}/trade/v1/loginNelogicaMobile`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'access-token': accessToken
        }
      }
    ).then(res => res.json());
    const searchParams = new URLSearchParams(
      new URL(data.button.url).searchParams
    );

    const code = searchParams.get('code');
    const isMigrated = searchParams.get('isRena');

    window.location.href = `${redirectUrl}?code=${code}&isRena=${isMigrated}`;
  } catch (error) {
    logError(error, 'hb_login_generate_nelo_token_error');

    notify({
      group: 'wrn-notifications',
      text: `Ocorreu um problema. Tente novamente.`,
      type: NotificationTypes.error
    });
  }
};

const handleLogin = async () => {
  try {
    isLoading.value = true;

    const { accessToken } = await login({
      email: formData.value.email.value,
      password: formData.value.password.value,
      token: recaptchaToken.value
    });

    await generateHBToken(accessToken);
  } catch (err) {
    const error = err as any;
    logError(error, 'hb_login_error');

    if (error?.message === 'error_login_auth_token_verify') {
      mfaInfo.value = error.custom as IMFAInfo;
    } else {
      notify({
        group: 'wrn-notifications',
        text: `Ocorreu um problema. Tente novamente.`,
        type: NotificationTypes.error
      });
    }
  } finally {
    isLoading.value = false;
  }
};

const handleSubmit = () => {
  validateFields();
  if (hasFieldError.value) return;

  recaptcha.value?.onChallenge(RecaptchaActions.SIGN_IN);
};
</script>

<style lang="less" scoped>
.login {
  &__main {
    display: flex;
    flex-grow: 1;
    width: 100%;
    max-width: calc(360px + @size-spacing-x400 * 2);
    padding: @size-spacing-x600 @size-spacing-x400;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media screen and (max-width: 768px) {
      justify-content: flex-start;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
  }

  &__heading {
    margin-bottom: @size-spacing-x700;
  }

  &__input {
    margin-bottom: @size-spacing-x500;
  }

  &__divider {
    border-top: var(--size-border-x400) solid var(--divider-primary);
    width: 100%;
    margin: @size-spacing-x500 0;
  }

  &__links {
    display: flex;
    flex-direction: column;
  }

  &__link {
    justify-self: center;
    align-self: center;
    margin-bottom: @size-spacing-x400;
  }

  &__logo {
    :deep(svg path) {
      fill: @element-over-image !important;
    }
  }
}
</style>
